import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import config from "./config";
import DisclosureIcon from "./DisclosureIcon";
import { usePersonalServices } from "./personalServices";
import { APPEARANCE_CHANGE_EVENT, getAppearancePreference, saveAppearancePreference } from "./plansAppearance";
import { incidentPreview, incidentState, incidentLabel, incidentText, incidentReportLink } from "./serviceIncidentPresentation";
import { serviceHistoryPath } from "./serviceHistoryRoutes";
import "./Services.css";

export default function Services() {
  const { services, loading, refreshing, error, savingId, lastSynced, refresh, setSelected } = usePersonalServices();
  const [appearance, setAppearance] = useState(getAppearancePreference);
  const [details, setDetails] = useState(null);
  const [detailsError, setDetailsError] = useState("");
  const location = useLocation();
  const navigate = useNavigate();
  const serviceId = new URLSearchParams(location.search).get("serviceId");

  useEffect(() => {
    const update = () => setAppearance(getAppearancePreference());
    window.addEventListener(APPEARANCE_CHANGE_EVENT, update);
    window.addEventListener("storage", update);
    return () => {
      window.removeEventListener(APPEARANCE_CHANGE_EVENT, update);
      window.removeEventListener("storage", update);
    };
  }, []);

  useEffect(() => {
    setDetails(null);
    setDetailsError("");
    if (!serviceId) return;
    const controller = new AbortController();
    let cancelled = false;
    const options = { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }, signal: controller.signal, timeout: 15000 };
    // Saved links resolve against the active catalog, never URL-supplied names.
    Promise.all([
      axios.get(`${config.API_URL}api/User/GetServiceStatus/${encodeURIComponent(serviceId)}`, options),
      axios.get(`${config.API_URL}api/User/GetServiceIncidents/${encodeURIComponent(serviceId)}`, options),
    ]).then(([status, incidents]) => {
      if (cancelled) return;
      setDetails({ name: status.data.DisplayName, incidents: incidentPreview(incidents.data) });
    }).catch(() => {
      if (!cancelled) setDetailsError("This service is unavailable or is no longer monitored.");
    });
    return () => { cancelled = true; controller.abort(); };
  }, [serviceId]);

  const monitored = services.filter(service => service.IsSelected).length;
  return (
    <main className={`service-monitor-page service-monitor-page--${appearance}`}>
      <div className="service-monitor-inner">
        <header className="service-monitor-header">
          <div>
            <p className="service-monitor-eyebrow">YOUR SERVICES</p>
            <h1>Keep an eye on what keeps you connected.</h1>
            <p className="service-monitor-intro">Choose the services you want to monitor. Your personal selections stay in sync with the SignalTuner Windows app.</p>
          </div>
          <div className="service-monitor-theme" role="group" aria-label="Website appearance">
            {["light", "dark"].map(theme => <button key={theme} type="button" aria-pressed={appearance === theme}
              onClick={() => saveAppearancePreference(theme)}>{theme === "light" ? "Light" : "Dark"}</button>)}
          </div>
        </header>
        <section className="service-monitor-panel" aria-label="Personal service monitoring">
          <div className="service-monitor-toolbar">
            <div><h2>Service monitoring</h2><p>{loading ? "Loading your selections…" : `${monitored} of ${services.length} services monitored`}</p></div>
            <button className="service-monitor-button" type="button" onClick={refresh} disabled={refreshing || savingId !== null}>{refreshing ? "Syncing…" : "Refresh"}</button>
          </div>
          {error && <p className="service-monitor-alert" role="alert">{error}</p>}
          {detailsError && <p className="service-monitor-alert" role="alert">{detailsError}</p>}
          {loading && <div className="service-monitor-empty" role="status">Loading services…</div>}
          {!loading && !services.length && <div className="service-monitor-empty">{error ? "Services could not be loaded." : "No services are available right now. Check back soon."}</div>}
          <ul className="service-monitor-list" aria-label="Services">
            {services.map(service => {
              const expanded = String(service.ServiceId) === serviceId;
              const status = service.ServiceCurrentStatus || "Status unavailable";
              const statusStyle = status === "Operational" ? "healthy" : status === "Outage" ? "outage" : status === "Active incident" ? "incident" : "unknown";
              const saving = savingId === service.ServiceId;
              return <li key={service.ServiceId} className={`service-monitor-row${service.IsSelected ? " is-monitored" : ""}`} data-service-status={statusStyle}>
                <div className="service-monitor-row-main">
                  <div className="service-monitor-identity">
                    <span className="service-monitor-logo" aria-hidden="true"><span>{service.ServiceDisplayName.slice(0, 1)}</span>
                      <img src={`https://www.signaltuner.com/images/serviceicons/${encodeURIComponent(service.ServiceName)}.png`} alt="" onError={event => { event.currentTarget.style.display = "none"; }} />
                    </span>
                    <div><h3>{service.ServiceDisplayName}</h3><span className={`service-monitor-status service-monitor-status--${statusStyle}`}><span aria-hidden="true" />{status}</span></div>
                  </div>
                  <button className="service-monitor-details-button" type="button" aria-expanded={expanded} aria-controls={`service-incidents-${service.ServiceId}`}
                    onClick={() => navigate(expanded ? "/services" : `/services?serviceId=${service.ServiceId}`, { replace: true })}>
                    {expanded ? "Hide incidents" : "View incidents"}<DisclosureIcon />
                  </button>
                  <label className="service-monitor-selection"><span>{saving ? "Saving…" : service.IsSelected ? "Monitoring on" : "Monitoring off"}</span>
                    <input type="checkbox" role="switch" aria-label={`Monitor ${service.ServiceDisplayName}`} checked={service.IsSelected}
                      disabled={savingId !== null} onChange={event => setSelected(service.ServiceId, event.target.checked)} />
                    <span className="service-monitor-switch" aria-hidden="true" />
                  </label>
                </div>
                {expanded && <section className="service-monitor-incidents" id={`service-incidents-${service.ServiceId}`} aria-label={`Incidents for ${service.ServiceDisplayName}`}>
                  {!details && !detailsError && <p role="status">Loading incidents…</p>}
                  {details && <><h4>Incidents for {details.name}</h4>
                    {!details.incidents.length && <p>No incidents to display.</p>}
                    {details.incidents.map(incident => <article key={incident.IncidentId} data-incident-state={incidentState(incident.Status)}>
                      <div className="service-monitor-incident-heading"><h5>{incident.Title}</h5><span className="service-monitor-incident-label">{incidentLabel(incident.Status)}</span></div>
                      <p className="service-monitor-date">Updated {new Date(incident.PublishDate).toLocaleString()}</p>
                      <p className="service-monitor-description">{incidentText(incident.Description)}</p>
                      {incidentReportLink(incident.Link) && <a href={incidentReportLink(incident.Link)} target="_blank" rel="noopener noreferrer">Read provider report <span aria-hidden="true">↗</span></a>}
                    </article>)}
                    <Link className="service-monitor-history-link" to={serviceHistoryPath(service.ServiceId)}>See full report history</Link>
                  </>}
                </section>}
              </li>;
            })}
          </ul>
          <footer className="service-monitor-footer">
            <span className="service-monitor-sync" role="status">{savingId !== null ? "Saving your selection…" : lastSynced ? `Synced at ${lastSynced.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}` : "Waiting to sync"}</span>
            <span>Same account. Same selections. Automatically refreshed every 30 seconds.</span>
          </footer>
        </section>
        <p className="service-monitor-note">Monitoring enables personal service alerts. Existing team-managed alerts are separate.</p>
      </div>
    </main>
  );
}
